import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const SOURCES = ["Referral", "Event", "Social Media", "WhatsApp", "LinkedIn", "Google", "Friend", "Other"];

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", business_name: "", email: "", mobile: "", password: "",
    referral_source: "", chapter: "", organisation: "", referral_name: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Sign up failed");
    }
    setLoading(false);
  };

  return (
    <AuthShell title="Create your account" subtitle="Start with the DynaQR Solo plan — ₹499/year">
      <form onSubmit={submit} className="space-y-4">
        {error && <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>}
        <div className="space-y-2">
          <Label>Full name</Label>
          <Input data-testid="signup-name" value={form.full_name} onChange={set("full_name")} required />
        </div>
        <div className="space-y-2">
          <Label>Business / Company name</Label>
          <Input data-testid="signup-business" value={form.business_name} onChange={set("business_name")} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" data-testid="signup-email" value={form.email} onChange={set("email")} required />
          </div>
          <div className="space-y-2">
            <Label>Mobile</Label>
            <Input data-testid="signup-mobile" value={form.mobile} onChange={set("mobile")} required />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Password</Label>
          <Input type="password" data-testid="signup-password" value={form.password} onChange={set("password")} required />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>How did you hear about us?</Label>
            <Select value={form.referral_source} onValueChange={(v) => setForm({ ...form, referral_source: v })}>
              <SelectTrigger data-testid="signup-source"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Organisation / Chapter</Label>
            <Input data-testid="signup-chapter" placeholder="e.g. Your group or team" value={form.chapter} onChange={set("chapter")} />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="signup-submit">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create account
        </Button>
      </form>
      <p className="mt-5 text-center text-sm text-muted-foreground">
        Already have an account? <Link to="/login" className="text-accent hover:underline font-medium">Log in</Link>
      </p>
    </AuthShell>
  );
}
